export type CollectionCardActionType = 'selectVase' | 'viewModel';

export interface CollectionCardActionBinding {
  action: CollectionCardActionType;
  target: string;
  label?: string;
  useTargetOverride?: boolean;
}

export interface CollectionCardActionBindings {
  primaryAction: CollectionCardActionBinding;
  secondaryAction: CollectionCardActionBinding;
}

const DEFAULT_SELECT_LABEL = 'Select';
const DEFAULT_VIEW_LABEL = 'View in 3D';

function coerceActionBinding(
  value: unknown,
  fallbackAction: CollectionCardActionType,
  fallbackTarget: string,
  fallbackLabel: string
): CollectionCardActionBinding {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {
      action: fallbackAction,
      target: fallbackTarget,
      label: fallbackLabel,
    };
  }

  const record = value as Record<string, unknown>;
  const action = record.action === 'selectVase' || record.action === 'viewModel'
    ? record.action
    : fallbackAction;
  const target =
    typeof record.target === 'string'
      ? record.target.trim()
      : record.target !== undefined && record.target !== null
        ? String(record.target).trim()
        : fallbackTarget;
  const label =
    typeof record.label === 'string' && record.label.trim().length > 0
      ? record.label.trim()
      : fallbackLabel;

  return {
    action,
    target: target || fallbackTarget,
    label,
    useTargetOverride: record.useTargetOverride === true,
  };
}

export function createCollectionCardActionBindings(
  modelReference: number,
  modelPath?: string
): CollectionCardActionBindings {
  return {
    primaryAction: {
      action: 'selectVase',
      target: String(modelReference),
      label: DEFAULT_SELECT_LABEL,
      useTargetOverride: false,
    },
    secondaryAction: {
      action: 'viewModel',
      target: typeof modelPath === 'string' ? modelPath.trim() : '',
      label: DEFAULT_VIEW_LABEL,
      useTargetOverride: false,
    },
  };
}

export function resolveCollectionCardActionBindings(
  content: Record<string, unknown>,
  fallbackModelReference: number,
  fallbackModelPath?: string
): CollectionCardActionBindings {
  const fallback = createCollectionCardActionBindings(
    fallbackModelReference,
    fallbackModelPath
  );

  return {
    primaryAction: coerceActionBinding(
      content.primaryAction,
      fallback.primaryAction.action,
      fallback.primaryAction.target,
      fallback.primaryAction.label ?? DEFAULT_SELECT_LABEL
    ),
    secondaryAction: coerceActionBinding(
      content.secondaryAction,
      fallback.secondaryAction.action,
      fallback.secondaryAction.target,
      fallback.secondaryAction.label ?? DEFAULT_VIEW_LABEL
    ),
  };
}

export function normalizeCollectionCardActionContent(
  content: Record<string, unknown>,
  fallbackModelReference: number,
  fallbackModelPath?: string
): Record<string, unknown> {
  const actions = resolveCollectionCardActionBindings(
    content,
    fallbackModelReference,
    fallbackModelPath
  );

  return {
    ...content,
    primaryAction: actions.primaryAction,
    secondaryAction: actions.secondaryAction,
  };
}
